import { useParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

export default function Session() {
  useSeo({ title: "Testing session", noindex: true });
  const { token } = useParams();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["session", token], queryFn: () => api.session(token) });
  const [view, setView] = useState(null);
  const [finding, setFinding] = useState({ case_ref: "", title: "", steps: "", expected: "", actual: "", severity_reported: "S3" });
  const [showSheet, setShowSheet] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(null);

  async function onFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (!/^(image|video)\//.test(f.type)) { toast.error(`${f.name}: only images and screen recordings`); continue; }
      if (f.size > 200 * 1024 * 1024) { toast.error(`${f.name}: over 200 MB`); continue; }
      setUploading({ name: f.name, pct: 0 });
      try {
        const m = await api.uploadMedia(token, f, (pct) => setUploading({ name: f.name, pct }));
        setMedia((prev) => [...prev, m]);
        toast.success(`${f.name} attached`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    setUploading(null);
  }

  if (isLoading) return <div className="app-main"><div className="wrap"><p>Loading offer…</p></div></div>;
  if (!data) return <div className="app-main"><div className="wrap"><h1>Offer unavailable</h1></div></div>;

  const s = data.session;
  const state = view || s.state;

  async function accept() {
    const r = await api.sessionAccept(token);
    if (r.ok) { toast.success("Session is yours"); setView("accepted"); refetch(); }
    else toast.error(r.reason === "filled" ? "Someone got there first" : r.reason);
  }
  async function start() {
    const dev = data.tester?.devices?.[0] || { platform: "android", form: "phone" };
    const r = await api.sessionStart(token, { device_used: dev });
    if (r.ok) { toast.success("Timer started"); setView("started"); refetch(); }
    else toast.error(r.reason);
  }
  async function markCase(cid, result) {
    await api.sessionCase(token, { case_id: cid, result });
    toast.success(`${cid} → ${result}`);
    if (result === "fail") { setFinding({ ...finding, case_ref: cid }); setShowSheet(true); }
    refetch();
  }
  async function saveFinding() {
    if (!finding.title || finding.title.length < 8) { toast.error("Give the finding a clear title"); return; }
    if (uploading) { toast.error("Wait for the upload to finish"); return; }
    if (["S1", "S2"].includes(finding.severity_reported) && media.length === 0) { toast.error("S1/S2 findings need a screenshot or screen recording"); return; }
    const r = await api.sessionFinding(token, { ...finding, media });
    if (r.ok) { toast.success("Finding saved"); setShowSheet(false); setMedia([]); setFinding({ case_ref: "", title: "", steps: "", expected: "", actual: "", severity_reported: "S3" }); refetch(); }
    else toast.error("Could not save");
  }
  async function submit() {
    const r = await api.sessionSubmit(token);
    if (r.ok) { toast.success("Submitted"); setView("submitted"); refetch(); }
    else toast.error(r.reason);
  }

  return (
    <>
      <header className="site-header"><div className="wrap"><a className="logo" href="/"><i></i>99tests</a>
        <nav><span className="muted small">Session {s.slot_index} · {data.job.code}</span></nav></div></header>
      <main className="app-main"><div className="wrap" style={{ maxWidth: 720 }}>

        {state === "offered" && (
          <div data-testid="offer-view">
            <span className="pill neutral">Offer</span>
            <h1 style={{ marginTop: 12 }}>{data.band_minutes}-minute session on {deviceLabel(s.device_requirement)}</h1>
            <p className="lede">{s.charter}</p>
            <div className="kpis" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className="kpi"><b>{money(data.fee_paise, "INR")}</b><span>fixed session fee</span></div>
              <div className="kpi"><b>+ ₹100–500</b><span>per accepted bug</span></div>
            </div>
            <button className="btn green" data-testid="accept-session-btn" onClick={accept}>Accept session</button>
          </div>
        )}

        {state === "accepted" && (
          <div data-testid="start-view">
            <span className="pill">Accepted</span>
            <h1 style={{ fontSize: "clamp(26px,6vw,34px)", marginTop: 12 }}>Confirm your device and start.</h1>
            <div className="panel-soft" style={{ padding: 18, margin: "18px 0" }}>
              <h4>Build</h4><p className="small">{data.job.app_url || "—"}</p>
              {data.job.credentials_note && <p className="hint">Credentials: {data.job.credentials_note}</p>}
            </div>
            <button className="btn green" data-testid="start-session-btn" onClick={start}>Start {data.band_minutes}-minute session</button>
          </div>
        )}

        {state === "started" && (
          <div data-testid="work-view">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
              <div><div className="muted small">Findings</div><div className="timer">{data.findings?.length || 0}</div></div>
            </div>
            <div className="charter" style={{ background: "var(--green-soft)", borderRadius: "var(--r-m)", padding: "16px 18px", borderLeft: "4px solid var(--green)" }}>
              <b>Charter</b><p style={{ margin: "6px 0 0", fontSize: 15 }}>{s.charter}</p>
            </div>
            <h3 style={{ margin: "26px 0 8px" }}>Test cases</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {data.cases.map((c) => (
                <li key={c.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, padding: "12px 0", borderTop: "1px solid var(--rule)" }}>
                  <div><b>{c.id}</b> {c.title}{s.case_results?.[c.id] && <span className="tag" style={{ marginLeft: 8 }}>{s.case_results[c.id]}</span>}</div>
                  <span style={{ display: "inline-flex", gap: 4 }}>
                    <button className="btn sm ghost" data-testid={`case-pass-${c.id}`} onClick={() => markCase(c.id, "pass")}>✓</button>
                    <button className="btn sm ghost" data-testid={`case-fail-${c.id}`} onClick={() => markCase(c.id, "fail")}>✕</button>
                    <button className="btn sm ghost" onClick={() => markCase(c.id, "blocked")}>!</button>
                  </span>
                </li>
              ))}
            </ul>
            <button className="btn ghost sm" data-testid="add-finding-btn" style={{ marginTop: 14 }} onClick={() => { setFinding({ case_ref: "", title: "", steps: "", expected: "", actual: "", severity_reported: "S3" }); setShowSheet(true); }}>Add a finding</button>
            <div style={{ marginTop: 24 }}><button className="btn" data-testid="submit-session-btn" onClick={submit}>Submit session</button></div>
          </div>
        )}

        {state === "submitted" && (
          <div data-testid="done-view">
            <span className="pill">Submitted</span>
            <h1 style={{ fontSize: "clamp(26px,6vw,34px)", marginTop: 12 }}>Thanks — that's in.</h1>
            <p className="lede">Your session fee of {money(data.fee_paise, "INR")} is confirmed. Bonuses are paid once the report is delivered and your findings are verified.</p>
          </div>
        )}

        {showSheet && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(14,42,34,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 30 }}>
            <div className="panel" style={{ background: "var(--paper)", width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", padding: 22 }} data-testid="finding-sheet">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}><h3>New finding</h3><button className="btn ghost sm" onClick={() => setShowSheet(false)}>Close</button></div>
              <div className="field"><label>Title</label><input data-testid="finding-title" value={finding.title} onChange={(e) => setFinding({ ...finding, title: e.target.value })} placeholder="Cart empties when returning from UPI app" /></div>
              <div className="field"><label>Steps to reproduce</label><textarea data-testid="finding-steps" value={finding.steps} onChange={(e) => setFinding({ ...finding, steps: e.target.value })} /></div>
              <div className="row">
                <div className="field"><label>Expected</label><textarea style={{ minHeight: 70 }} value={finding.expected} onChange={(e) => setFinding({ ...finding, expected: e.target.value })} /></div>
                <div className="field"><label>Actual</label><textarea style={{ minHeight: 70 }} value={finding.actual} onChange={(e) => setFinding({ ...finding, actual: e.target.value })} /></div>
              </div>
              <div className="field"><label>Severity</label>
                <select data-testid="finding-severity" value={finding.severity_reported} onChange={(e) => setFinding({ ...finding, severity_reported: e.target.value })}>
                  <option value="S1">S1 · Blocks / data loss</option><option value="S2">S2 · Major, no workaround</option>
                  <option value="S3">S3 · Minor, workaround</option><option value="S4">S4 · Cosmetic</option></select>
                <p className="hint">S1/S2 require a screenshot or screen recording. Verification may adjust severity against the rubric.</p></div>
              <div className="field"><label>Media</label>
                <input type="file" data-testid="finding-media-input" accept="image/*,video/*" multiple onChange={onFiles} disabled={!!uploading} />
                {uploading && <p className="hint" data-testid="upload-progress">Uploading {uploading.name}… {uploading.pct}%</p>}
                {media.length > 0 && (
                  <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 6 }} data-testid="finding-media-list">
                    {media.map((m) => (
                      <li key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14 }}>
                        <span><span className="tag">{m.content_type.startsWith("video/") ? "recording" : "screenshot"}</span> {m.name}</span>
                        <button type="button" className="btn ghost sm" onClick={() => setMedia(media.filter((x) => x.id !== m.id))}>Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="btn block" data-testid="save-finding-btn" onClick={saveFinding} disabled={!!uploading}>Save finding</button>
            </div>
          </div>
        )}
      </div></main>
    </>
  );
}

function deviceLabel(req) {
  if (!req) return "a device";
  if (req.platform === "web") return `${req.browser || "browser"} on ${req.os || "laptop"}`;
  return `${req.brand || ""} ${req.platform === "android" ? "Android" : "iOS"}`.trim();
}
